/** RaiderMapCR - Food recommendations page with filters */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Chip,
  CircularProgress,
  Rating,
  Paper,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
  Button,
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  StarRate as StarRateIcon
} from '@mui/icons-material';

const FoodServicesPage = () => {
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [filteredRestaurants, setFilteredRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [maxPrice, setMaxPrice] = useState('$$');
  const [selectedLocations, setSelectedLocations] = useState(['In Avenida Escazú', 'Delivery']);
  const [minRating, setMinRating] = useState(3);
  const [openNow, setOpenNow] = useState(false);
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [availableCuisines, setAvailableCuisines] = useState([]);

  const priceOptions = useMemo(() => ['$', '$$', '$$$', '$$$$'], []);
  const ratingOptions = [1, 2, 3, 4, 4.5];

  const getIncludedPrices = useCallback((maxPriceLevel) => {
    const index = priceOptions.indexOf(maxPriceLevel);
    return priceOptions.slice(0, index + 1);
  }, [priceOptions]);

  const extractUniqueCuisines = (restaurants) => {
    const cuisines = restaurants
      .map(restaurant => restaurant.cuisine)
      .filter(cuisine => cuisine && cuisine.trim() !== '')
      .filter((cuisine, index, array) => array.indexOf(cuisine) === index)
      .sort();
    return cuisines;
  };

  useEffect(() => {
    fetch('/api/admin/restaurants')
      .then(response => response.json())
      .then(data => {
        setAllRestaurants(data);
        setFilteredRestaurants(data);
        const cuisines = extractUniqueCuisines(data);
        setAvailableCuisines(cuisines);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching restaurants:', error);
        setLoading(false);
      });
  }, []);

  const isOpenNow = (hours) => {
    const now = new Date();
    const currentHour = now.getHours();
    if (hours.includes('24/7')) return true;
    if (hours.includes('Closed')) return false;
    const match = hours.match(/(\d+):(\d+)\s*(AM|PM)\s*-\s*(\d+):(\d+)\s*(AM|PM)/);
    if (match) {
      let openHour = parseInt(match[1]);
      let closeHour = parseInt(match[4]);

      if (match[3] === 'PM' && openHour !== 12) openHour += 12;
      if (match[6] === 'PM' && closeHour !== 12) closeHour += 12;

      return currentHour >= openHour && currentHour < closeHour;
    }

    return true;
  };

  useEffect(() => {
    let filtered = allRestaurants.filter(restaurant => {
      const allowedPrices = getIncludedPrices(maxPrice);
      if (!allowedPrices.includes(restaurant.price_range)) return false;
      if (selectedLocations.length > 0 && !selectedLocations.includes(restaurant.location)) return false;
      if (restaurant.rating < minRating) return false;
      if (openNow && !isOpenNow(restaurant.hours)) return false;
      if (selectedCuisines.length > 0 && !selectedCuisines.includes(restaurant.cuisine)) return false;

      return true;
    });

    setFilteredRestaurants(filtered);
  }, [allRestaurants, maxPrice, selectedLocations, minRating, openNow, selectedCuisines, getIncludedPrices]);

  const handlePriceChange = (selectedPrice) => {
    setMaxPrice(selectedPrice);
  };

  const handleRatingChange = (selectedRating) => {
    setMinRating(selectedRating);
  };

  const handleCuisineToggle = (cuisine) => {
    setSelectedCuisines(prev =>
      prev.includes(cuisine)
        ? prev.filter(c => c !== cuisine)
        : [...prev, cuisine]
    );
  };

  const handleLocationToggle = (location) => {
    setSelectedLocations(prev =>
      prev.includes(location)
        ? prev.filter(l => l !== location)
        : [...prev, location]
    );
  };

  if (loading) {
    return (
      <div className="main-content">
        <Container>
          <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
            <CircularProgress />
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className="main-content">
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <RestaurantIcon sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
          <Typography variant="h3" component="h1" gutterBottom sx={{ color: '#ffffff' }}>
            Food Recommendations
          </Typography>
          <Typography variant="h6" sx={{ color: '#ffffff' }}>
            Discover the best dining options near campus
          </Typography>
        </Box>

        <Paper sx={{
          mb: 3,
          p: 2,
          borderRadius: '12px',
          backgroundColor: 'rgba(42, 42, 42, 0.85)',
          border: '1px solid rgba(74, 74, 74, 0.6)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
        }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              borderRadius: '8px',
              p: 1,
              mx: -1,
              transition: 'all 0.1s ease',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
              '&:active': {
                transform: 'scale(0.99)',
              }
            }}
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterIcon sx={{ mr: 1, color: '#ffffff' }} />
              <Typography variant="h6" sx={{ color: '#ffffff' }}>
                Filters
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', color: '#ffffff' }}>
              {filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Box>
          </Box>

          <Collapse in={filtersOpen}>
            <Grid container spacing={3} sx={{ mt: 1 }} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <FormControl component="fieldset" sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <FormLabel component="legend" sx={{ color: '#ffffff', mb: 1 }}>Price Level</FormLabel>
                  <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    <ToggleButtonGroup
                      value={maxPrice}
                      exclusive
                      onChange={(event, newValue) => newValue && handlePriceChange(newValue)}
                      aria-label="max price level"
                      size="small"
                      sx={{ flexWrap: 'wrap', gap: 0.5 }}
                    >
                      {priceOptions.map((price) => (
                        <ToggleButton
                          key={price}
                          value={price}
                          sx={{
                            backgroundColor: getIncludedPrices(maxPrice).includes(price) ? 'primary.main' : 'rgba(42, 42, 42, 0.85)',
                            color: getIncludedPrices(maxPrice).includes(price) ? '#ffffff' : '#ffffff',
                            border: '1px solid rgba(74, 74, 74, 0.6)',
                            transition: 'all 0.1s ease',
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: '#ffffff',
                              '&:hover': {
                                backgroundColor: 'primary.main',
                              }
                            },
                            '&:hover': {
                              backgroundColor: getIncludedPrices(maxPrice).includes(price) ? 'primary.main' : 'rgba(74, 74, 74, 0.3)',
                            },
                            '&:active': {
                              transform: 'scale(0.98)',
                            }
                          }}
                        >
                          {price}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl component="fieldset" sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <FormLabel component="legend" sx={{ color: '#ffffff', mb: 1 }}>Minimum Rating</FormLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    {ratingOptions.map((rating) => (
                      <Chip
                        key={rating}
                        label={`${rating}★`}
                        clickable
                        variant={rating <= minRating ? 'filled' : 'outlined'}
                        color={rating <= minRating ? 'warning' : 'default'}
                        onClick={() => handleRatingChange(rating)}
                        size="small"
                        sx={{
                          minWidth: '45px',
                          transition: 'all 0.1s ease',
                          '& .MuiChip-label': {
                            fontSize: '0.75rem',
                            fontWeight: rating === minRating ? 600 : 400
                          },
                          '&:hover': {
                            transform: 'translateY(-1px)',
                          },
                          '&:active': {
                            transform: 'scale(0.95)',
                          }
                        }}
                      />
                    ))}
                  </Box>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl component="fieldset" sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <FormGroup sx={{ alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedLocations.includes('In Avenida Escazú')}
                          onChange={() => handleLocationToggle('In Avenida Escazú')}
                          sx={{
                            color: '#ffffff',
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                            '& .MuiSvgIcon-root': {
                              transition: 'all 0.1s ease',
                            },
                            '&:active': {
                              transform: 'scale(0.9)',
                            }
                          }}
                        />
                      }
                      label={<span style={{ color: '#ffffff' }}>In Avenida Escazú</span>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedLocations.includes('Delivery')}
                          onChange={() => handleLocationToggle('Delivery')}
                          sx={{
                            color: '#ffffff',
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                            '& .MuiSvgIcon-root': {
                              transition: 'all 0.1s ease',
                            },
                            '&:active': {
                              transform: 'scale(0.9)',
                            }
                          }}
                        />
                      }
                      label={<span style={{ color: '#ffffff' }}>Delivery</span>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={openNow}
                          onChange={(e) => setOpenNow(e.target.checked)}
                          sx={{
                            color: '#ffffff',
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                            '& .MuiSvgIcon-root': {
                              transition: 'all 0.1s ease',
                            },
                            '&:active': {
                              transform: 'scale(0.9)',
                            }
                          }}
                        />
                      }
                      label={<span style={{ color: '#ffffff' }}>Open Now</span>}
                    />
                  </FormGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} sx={{ pt: 2, borderTop: '1px solid rgba(74, 74, 74, 0.4)', mt: 1 }}>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <FormLabel component="legend" sx={{ color: '#ffffff', mb: 1, display: 'block' }}>Type</FormLabel>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {availableCuisines.length > 0 ? (
                      availableCuisines.map((cuisine) => (
                        <Chip
                          key={cuisine}
                          label={cuisine}
                          clickable
                          variant="outlined"
                          onClick={() => handleCuisineToggle(cuisine)}
                          size="small"
                          sx={{
                            backgroundColor: selectedCuisines.includes(cuisine) ? 'primary.main' : 'rgba(42, 42, 42, 0.85)',
                            color: '#ffffff',
                            border: '1px solid rgba(74, 74, 74, 0.6)',
                            transition: 'all 0.1s ease',
                            '&:hover': {
                              backgroundColor: selectedCuisines.includes(cuisine) ? 'primary.main' : 'rgba(74, 74, 74, 0.3)',
                            },
                            '&:active': {
                              transform: 'scale(0.98)',
                            }
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', width: '100%' }}>
                        No cuisine data available
                      </Typography>
                    )}
                  </Box>
                </FormControl>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>

        <Grid container spacing={3}>
          {filteredRestaurants.length === 0 ? (
            <Grid item xs={12}>
              <Card sx={{
                display: 'flex',
                backgroundColor: 'rgba(42, 42, 42, 0.85)',
                border: '1px solid rgba(74, 74, 74, 0.6)',
                backdropFilter: 'blur(12px)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
              }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', flexGrow: 1 }}>
                  <Typography variant="h6" sx={{ color: '#ffffff' }}>
                    {allRestaurants.length === 0
                      ? 'Restaurant information coming soon...'
                      : 'No restaurants match your current filters.'
                    }
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: '#ffffff' }}>
                    {allRestaurants.length === 0
                      ? 'I\'m compiling a list of the best dining options near campus.'
                      : 'Try adjusting your filters to see more options.'
                    }
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ) : (
            filteredRestaurants.map((restaurant, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{
                  height: '100%',
                  display: 'flex',
                  backgroundColor: 'rgba(42, 42, 42, 0.85)',
                  border: '1px solid rgba(74, 74, 74, 0.6)',
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                  }
                }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexGrow: 1 }}>
                    <Typography variant="h6" component="h2" sx={{ color: '#ffffff', mb: -1 }}>
                      {restaurant.name}
                    </Typography>

                    <Box sx={{ my: 2 }}>
                      <Chip
                        label={restaurant.cuisine}
                        size="small"
                        sx={{
                          mr: 1,
                          mb: 1,
                          backgroundColor: 'rgba(42, 42, 42, 0.85)',
                          color: '#ffffff',
                          border: '1px solid rgba(74, 74, 74, 0.6)'
                        }}
                      />
                      <Chip
                        label={restaurant.price_range}
                        size="small"
                        variant="outlined"
                        sx={{
                          mr: 1,
                          mb: 1,
                          backgroundColor: 'rgba(42, 42, 42, 0.85)',
                          color: '#ffffff',
                          border: '1px solid rgba(74, 74, 74, 0.6)'
                        }}
                      />
                    </Box>

                    <Typography variant="body2" sx={{ color: '#ffffff', paragraph: true, mb: 3 }}>
                      {restaurant.description}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationIcon sx={{ mr: 1, fontSize: 16, color: '#ffffff' }} />
                      <Typography variant="body2" sx={{ color: '#ffffff' }}>
                        {restaurant.location}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TimeIcon sx={{ mr: 1, fontSize: 16, color: '#ffffff' }} />
                      <Typography variant="body2" sx={{ color: '#ffffff' }}>
                        {restaurant.hours}
                      </Typography>
                      {isOpenNow(restaurant.hours) && (
                        <Chip
                          label="Open Now"
                          size="small"
                          color="success"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>

                    {restaurant.rating && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Rating
                            value={restaurant.rating}
                            readOnly
                            size="small"
                            sx={{
                              '& .MuiRating-iconEmpty': {
                                color: 'rgba(255, 255, 255, 0.3)',
                              },
                              '& .MuiRating-iconFilled': {
                                color: '#ffd700',
                              }
                            }}
                          />
                          <Typography variant="body2" sx={{ ml: 1, color: '#ffffff' }}>
                            ({restaurant.rating}/5)
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<StarRateIcon />}
                          sx={{
                            color: '#ffffff',
                            borderColor: 'rgba(74, 74, 74, 0.6)',
                            backgroundColor: 'rgba(42, 42, 42, 0.85)',
                            '&:hover': {
                              borderColor: 'primary.main',
                              backgroundColor: 'rgba(74, 74, 74, 0.3)',
                            }
                          }}
                        >
                          Rate
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Container>
    </div>
  );
};

export default FoodServicesPage;
